/**
 * Collection infinite scroll with sold-out products sorted last.
 *
 * Behaviour:
 *  - In-stock products stream into the grid as the shopper scrolls (infinite scroll).
 *  - Sold-out products are held back and appended only after ALL in-stock pages
 *    have been loaded, so they always sit at the very bottom of the collection.
 *  - On first render the controller keeps loading pages until at least one full
 *    page-size worth of IN-STOCK products is shown (data-page-size), matching the
 *    "minimum first shown" requirement even when page 1 contains sold-out items.
 *
 * The controller re-initialises itself after the theme's AJAX filtering swaps the
 * collection content, and degrades gracefully to normal pagination without JS.
 */
(function () {
  if (!('IntersectionObserver' in window) || !window.DOMParser) return;

  var SELECTOR_GRID = '[data-infinite-grid]';
  var SELECTOR_TILE = '.grid__item[data-product-id]';

  function InfiniteSort(grid) {
    this.grid = grid;
    this.sectionId = grid.getAttribute('data-section-id');
    this.pageSize = parseInt(grid.getAttribute('data-page-size'), 10) || 0;
    this.nextPage = (parseInt(grid.getAttribute('data-current-page'), 10) || 1) + 1;
    this.totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;

    // Sentinel / spinner / pagination are siblings of the grid inside the block wrapper.
    this.wrapper = grid.parentNode || document;
    this.scrollArea = this.wrapper.querySelector('[data-infinite-scroll]');
    this.sentinel = this.wrapper.querySelector('[data-infinite-sentinel]');
    this.spinner = this.wrapper.querySelector('[data-infinite-spinner]');
    this.pagination = this.wrapper.querySelector('.pagination');

    this.hasNext = this.nextPage <= this.totalPages;
    this.loading = false;
    this.finalised = false;
    this.soldOutBuffer = [];

    this.init();
  }

  InfiniteSort.prototype.init = function () {
    // Hide the classic pagination while JS drives the loading.
    if (this.pagination) this.pagination.style.display = 'none';

    // Move server-rendered sold-out tiles into the buffer straight away.
    this.harvestSoldOut(this.grid);

    // Reveal the remaining in-stock tiles now (AOS won't animate them reliably
    // once the grid is being rebuilt), keeping display consistent with the
    // force-revealed tiles that infinite scroll appends.
    var current = this.grid.querySelectorAll(SELECTOR_TILE);
    for (var i = 0; i < current.length; i++) this.reveal(current[i]);

    // Nothing more to load: reveal buffered sold-out immediately.
    if (!this.hasNext) {
      this.finalise();
    } else {
      this.observe();
      // Eagerly load pages until the first screen has a full page-size of in-stock.
      this.fillMinimum();
    }
  };

  // Count in-stock tiles currently displayed in the grid (sold-out are buffered).
  InfiniteSort.prototype.inStockCount = function () {
    return this.grid.querySelectorAll(SELECTOR_TILE).length;
  };

  // The theme's AOS animation (once + disableMutationObserver) never reveals
  // moved/appended tiles, so force them visible the way the theme's own dynamic
  // markup does — with the aos-animate class.
  InfiniteSort.prototype.reveal = function (tile) {
    tile.classList.add('aos-animate');
  };

  // Pull sold-out tiles out of a container and stash them (preserving order).
  InfiniteSort.prototype.harvestSoldOut = function (container) {
    var soldOut = container.querySelectorAll(SELECTOR_TILE + '[data-available="false"]');
    for (var i = 0; i < soldOut.length; i++) {
      this.reveal(soldOut[i]);
      this.soldOutBuffer.push(soldOut[i]);
      if (soldOut[i].parentNode) soldOut[i].parentNode.removeChild(soldOut[i]);
    }
  };

  InfiniteSort.prototype.observe = function () {
    if (!this.sentinel) return;
    var self = this;
    this.io = new IntersectionObserver(
      function (entries) {
        if (entries[0] && entries[0].isIntersecting) self.loadNext();
      },
      { rootMargin: '600px 0px' }
    );
    this.io.observe(this.sentinel);
  };

  InfiniteSort.prototype.fillMinimum = function () {
    var self = this;
    if (!this.hasNext || this.loading) return;
    if (this.inStockCount() >= this.pageSize) return;
    this.loadNext().then(function () {
      self.fillMinimum();
    });
  };

  InfiniteSort.prototype.buildUrl = function (page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.set('section_id', this.sectionId);
    params.set('page', page);
    return window.location.pathname + '?' + params.toString();
  };

  InfiniteSort.prototype.loadNext = function () {
    var self = this;
    if (this.loading || !this.hasNext) return Promise.resolve();
    this.loading = true;
    this.toggleSpinner(true);

    var page = this.nextPage;
    return fetch(this.buildUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Bad response ' + res.status);
        return res.text();
      })
      .then(function (html) {
        self.appendFromHtml(html);
        self.nextPage = page + 1;
        self.hasNext = self.hasNext && self.nextPage <= self.totalPages;
        self.loading = false;
        self.toggleSpinner(false);
        if (!self.hasNext) self.finalise();
      })
      .catch(function () {
        // Stop trying on error; fall back to whatever is already displayed.
        self.loading = false;
        self.hasNext = false;
        self.toggleSpinner(false);
        self.finalise();
      });
  };

  InfiniteSort.prototype.appendFromHtml = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var incomingGrid = doc.querySelector(SELECTOR_GRID) || doc.querySelector('.grid--uniform');
    if (!incomingGrid) return;

    var tiles = incomingGrid.querySelectorAll(SELECTOR_TILE);
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      this.reveal(tile);
      if (tile.getAttribute('data-available') === 'false') {
        this.soldOutBuffer.push(tile);
      } else {
        fragment.appendChild(tile);
      }
    }
    this.grid.appendChild(fragment);

    // Carry over quick-shop modals for the newly loaded products (deduped by id).
    this.appendModals(doc);
    this.reinitQuickShop();
  };

  // Quick-shop modals live as siblings of the grid; each product needs its own
  // #QuickShopModal-<id> present before the theme can lazily wire it up.
  InfiniteSort.prototype.appendModals = function (doc) {
    var modals = doc.querySelectorAll('.modal--quick-shop');
    if (!modals.length || this.wrapper === document) return;
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < modals.length; i++) {
      if (!document.getElementById(modals[i].id)) fragment.appendChild(modals[i]);
    }
    this.wrapper.appendChild(fragment);
  };

  // Re-bind the theme's quick-shop hover handlers to include appended products.
  InfiniteSort.prototype.reinitQuickShop = function () {
    if (window.theme && typeof window.theme.initQuickShop === 'function') {
      window.theme.initQuickShop();
    }
  };

  // All in-stock pages loaded: drop buffered sold-out tiles at the very bottom.
  InfiniteSort.prototype.finalise = function () {
    if (this.finalised) return;
    this.finalised = true;

    if (this.io && this.sentinel) this.io.unobserve(this.sentinel);
    this.toggleSpinner(false);

    if (this.soldOutBuffer.length) {
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < this.soldOutBuffer.length; i++) {
        fragment.appendChild(this.soldOutBuffer[i]);
      }
      this.grid.appendChild(fragment);
      // Sold-out tiles are now in the grid; wire their quick-shop hover handlers.
      this.reinitQuickShop();
    }
    this.soldOutBuffer = [];

    if (this.scrollArea) this.scrollArea.setAttribute('data-infinite-done', 'true');
  };

  InfiniteSort.prototype.toggleSpinner = function (show) {
    if (this.spinner) this.spinner.hidden = !show;
  };

  // ---- Bootstrapping -------------------------------------------------------

  function initGrids(root) {
    var scope = root || document;
    var grids = scope.querySelectorAll(SELECTOR_GRID + ':not([data-infinite-ready])');
    for (var i = 0; i < grids.length; i++) {
      grids[i].setAttribute('data-infinite-ready', 'true');
      new InfiniteSort(grids[i]);
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initGrids(document);

    // Re-initialise after the theme swaps collection content on filter changes.
    var host = document.getElementById('CollectionAjaxResult') || document.body;
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        initGrids(document);
      });
    });
    observer.observe(host, { childList: true, subtree: true });
  });
})();
